/**
 * Le corps de métier d'un article, d'après son nom : « quand on rajoute un
 * article, SwissTech le classe — la peinture dans la peinture, le placo et
 * les rails en plâtrerie, le carrelage dans le carrelage… ».
 *
 * <p>Ces mots clés ne s'affichent pas : ils servent seulement à reconnaître
 * un nom d'article écrit en français.
 */

export const METIER_RULES = [
  [
    'Carrelage / Faïence',
    [
      'carrel',
      'faïence',
      'faience',
      'grès',
      'gres',
      'mortier-colle',
      'mortier colle',
      'croisillon',
      'mosaïque',
      'mosaique',
      'baguette',
      'plinthe carrel',
      'joint epoxy',
      'joint époxy',
    ],
  ],
  [
    'Peinture',
    [
      'peinture',
      'peindre',
      'peintre',
      'glacis',
      'lasure',
      'sous-couche',
      'sous couche',
      'enduit',
      'laque',
      'vernis',
      'glycéro',
      'glycero',
      'acrylique',
    ],
  ],
  [
    'Plâtrerie / Cloisons',
    [
      'placo',
      'ba13',
      // « plâtr » : plâtre, plâtrier, plâtrerie ; « gyps » : gypserie, gypseur.
      'plâtr',
      'platr',
      'gyps',
      'laine de verre',
      'laine minérale',
      'laine minerale',
      'laine de roche',
      'cloison',
      'plaque de plâtre',
      'staff',
      'corniche',
      'rail',
      // « Montant M48 », pas « Montant forfaitaire ».
      'montant m',
      'montants m',
      'bande à joint',
      'lissage',
    ],
  ],
  [
    'Maçonnerie',
    [
      'maçon',
      'macon',
      'béton',
      'beton',
      'ciment',
      'chape',
      'crépi',
      'crepi',
      'parpaing',
      'brique',
      'scellement',
      'ragréage',
      'ragreage',
      'coffrage',
      'fondation',
    ],
  ],
  [
    'Plomberie / Sanitaire',
    [
      'plomb',
      'sanitaire',
      'wc',
      'lavabo',
      'évier',
      'evier',
      'siphon',
      'mitigeur',
      'robinet',
      'douche',
      'baignoire',
      'chauffe-eau',
      'tuyau',
      'raccord',
      'vidage',
      'bonde',
    ],
  ],
  [
    'Chauffage',
    [
      'chauffage',
      'radiateur',
      'chaudière',
      'chaudiere',
      'plancher chauffant',
      'thermostat',
      'pompe à chaleur',
      'brûleur',
      'bruleur',
      'circulateur',
    ],
  ],
  [
    'Ventilation / Clim.',
    ['ventilation', 'vmc', 'climatisation', 'gaine', 'extracteur', 'aération', 'aeration'],
  ],
  [
    'Électricité',
    [
      'électr',
      'electr',
      'câble',
      'cable',
      'prise',
      'interrupteur',
      'disjoncteur',
      'luminaire',
      'domotique',
    ],
  ],
  [
    'Menuiserie / Agencement',
    ['menuiserie', 'porte', 'plinthe bois', 'placard', 'agencement', 'quincaillerie', 'bloc-porte'],
  ],
  [
    'Couverture / Toiture',
    [
      'toiture',
      'tuile',
      'couverture',
      'zinguerie',
      'chéneau',
      'cheneau',
      'ardoise',
      'faîtage',
      'faitage',
    ],
  ],
  ['Étanchéité', ['étanch', 'etanch', 'membrane', 'bitume']],
  [
    'Fenêtres',
    [
      'porte-fenêtre',
      'porte-fenetre',
      'porte fenêtre',
      'fenêtre',
      'fenetre',
      'vitrage',
      'store',
      'volet',
      'châssis',
      'chassis',
      'baie vitrée',
    ],
  ],
  ['Vitrerie', ['vitre', 'miroir', 'verre', 'glace']],
  [
    'Serrurerie / Métallerie',
    ['serrur', 'métall', 'metall', 'garde-corps', 'rampe', 'portail', 'ferronnerie'],
  ],
  [
    'Sols / Parquet',
    [
      'parquet',
      'sol pvc',
      'lino',
      'linoléum',
      'linoleum',
      'moquette',
      'stratifié',
      'stratifie',
      'vinyle',
    ],
  ],
  ['Nettoyage / Fin de chantier', ['nettoyage', 'fin de chantier', 'débarras', 'debarras']],
];

/** Un mot clé compte seulement au début d'un mot : « prise » n'est pas dans « reprise ». */
const debutDeMot = (texte, cle) => {
  let i = texte.indexOf(cle);
  while (i > -1) {
    if (i === 0 || !/\p{L}/u.test(texte.charAt(i - 1))) return true;
    i = texte.indexOf(cle, i + 1);
  }
  return false;
};

/**
 * Le corps de métier d'un article, d'après son nom.
 *
 * <p>Le mot clé le plus long l'emporte : « Porte-fenêtre » va aux fenêtres
 * (pas à la menuiserie), « Peinture de la porte » à la peinture. Un mot clé
 * ne compte qu'au début d'un mot : « plaque » n'est pas de la laque, « mise
 * d'aplomb » pas de la plomberie, « reprise » pas une prise électrique.
 *
 * @param {string} nom nom de l'article
 * @returns {string|null} le corps de métier, ou null s'il n'est pas reconnu
 */
export function detectMetier(nom) {
  const n = (nom || '').toLowerCase();
  let meilleur = null;
  let longueur = 0;
  for (const [metier, cles] of METIER_RULES) {
    for (const cle of cles) {
      if (cle.length > longueur && debutDeMot(n, cle)) {
        meilleur = metier;
        longueur = cle.length;
      }
    }
  }
  return meilleur;
}
